import { FormArray, FormGroup } from '@angular/forms';
import { DynamicComponents } from '../dynamic/services/dynamic.service';
import { ServiceProvider } from './service-provider';
import { hasValidAddress } from '../has-valid-address';
import { environment } from 'src/environments/environment';
import { Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { easyFormatter } from '../easyFormatter';
import { map, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

export interface serviceProductCategory {
  _meta: { index: number; id: string; deleted?: boolean };
  name: string;
  value: string;
  disabled: boolean;
}

export interface serviceGroupedProducts {
  [key: string]: any[];
}

/** Commands the provider form's toolbar sends to this service's product table (#215). */
export type ProductSelectionCommand = 'enter' | 'exit' | 'confirm';

/**
 * Multi-select removal state for this service's product table. Lives on the
 * Service instance (shared by the provider form that renders the toolbar and
 * the dynamic component that renders the table) so neither has to reach into
 * the other's view, and so the parent template reads a stable object rather
 * than a child component created mid change-detection.
 */
export interface ProductSelectionState {
  active: boolean;
  busy: boolean;
  selected: Set<string>;
}

export interface DeleteProductsResponse {
  deleted: string[];
  notFound: string[];
  rows: number;
}

export class Service {
  private unsubscribe$ = new Subject<void>();

  private serviceWrapper: FormGroup;
  public serviceProvider: ServiceProvider;

  form: FormGroup;

  readonly productSelection: ProductSelectionState = {
    active: false,
    busy: false,
    selected: new Set<string>(),
  };

  readonly productSelectionCommand$ = new Subject<ProductSelectionCommand>();

  /** Live products in this service's form (0 when the type has none / isn't built yet). */
  get productCount(): number {
    const products = this.form.get('products') as FormArray | null;

    return products ? products.length : 0;
  }

  get _meta(): { id: string; index: number } | null {
    return this.serviceWrapper.get('_meta')?.value;
  }

  get type(): DynamicComponents | null {
    return this.serviceWrapper.get('type')?.value;
  }

  get serviceProviderName(): string {
    return this.serviceProvider.name;
  }

  get serviceProviderAddress(): any {
    return this.serviceProvider.address;
  }

  get serviceProviderObj(): any {
    return this.serviceProvider.obj;
  }

  /** The provider is tagged "Do not use for new bookings" (#248): pickers leave it out unless it's already selected. */
  get retired(): boolean {
    return !!this.serviceProvider?.isRetired;
  }

  get name(): string {
    const serviceNameControl = this.form.get('name');

    return serviceNameControl ? serviceNameControl.value : this.serviceProvider.name;
  }

  get address(): any {
    const serviceAddressControl = this.form.get('defaultAddress');

    if (!serviceAddressControl) {
      return this.serviceProvider.address;
    } else {
      const hasServiceAddress = hasValidAddress(serviceAddressControl.value);

      if (hasServiceAddress) {
        let needsPatch = false;

        const address = serviceAddressControl.value;

        // Fix Address: Some (most) default addresses don't have their state set. We check if
        // serviceAddressControl.value.street is set and serviceAddressControl.value.state is
        // null. If so, we add the state from this.serviceProvider.address.state

        // Note: This is a temp fix to ensure that the address is correct when the service. The
        // service provider should have the correct address set.
        if (
          hasServiceAddress &&
          serviceAddressControl.value.street &&
          !serviceAddressControl.value.state
        ) {
          needsPatch = true;
        }

        if (needsPatch) {
          address.state = this.serviceProvider.address.state;
        }

        return address;
      } else {
        return this.serviceProvider.address;
      }
    }
  }

  get contact(): any {
    const serviceContactControl = this.form.get('contact');

    return serviceContactControl ? serviceContactControl.value : this.serviceProvider.contact;
  }

  get serviceProviderId(): number | null {
    return this.serviceProvider.id;
  }

  constructor(
    serviceProvider: ServiceProvider,
    serviceWrapper: FormGroup,
    private http: HttpClient
  ) {
    this.serviceProvider = serviceProvider;
    this.serviceWrapper = serviceWrapper;

    this.form = new FormGroup({});
  }

  public getProductCategories(): serviceProductCategory[] {
    const categories: any[] = _.get(this.form.value, 'productCategories');

    return categories;
  }

  public getGroupedProducts(): serviceGroupedProducts {
    const products: any[] = _.get(this.form.value, 'products');

    const [withCategory, missingCategory] = _.partition(products, (product) =>
      _.has(product, 'category.value')
    );

    if (missingCategory.length) {
      console.warn(
        `Service '${this.name}' has ${missingCategory.length} product(s) missing a category.`,
        missingCategory
      );
    }

    const grouped = _.groupBy(withCategory, (product) => product.category.value);

    return grouped;
  }

  public saveProduct(product: any, serviceIndex: number, path: string): Observable<boolean> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.product.patch,
      { productId: _.get(product, '_meta.id') }
    );

    const data: any = {
      serviceProviderId: this.serviceProviderId,
      serviceMeta: this._meta,
      product: product,
      path: path,
    };

    return this.http.patch<any>(url, data).pipe(
      takeUntil(this.unsubscribe$),
      map((res) => {
        return res;
      })
    );
  }

  /**
   * Soft-delete products of THIS service on the server in one request (#215).
   * Caller chunks to the server's batch ceiling (see GenericServiceProvider).
   * DELETE with a JSON body, same shape as the media batch delete.
   */
  public deleteProducts(productIds: string[]): Observable<DeleteProductsResponse> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.serviceProvider.products.delete,
      { id: _.toString(this.serviceProviderId) }
    );

    return this.http
      .delete<{ data: DeleteProductsResponse }>(url, {
        body: { serviceMetaId: this._meta?.id, productIds },
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        map((res) => res.data)
      );
  }

  reset(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
